import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Accept, FileRejection } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { TextArea } from "@jhool-io/fe-components";
import * as FilePicker from "../../../../../shared-ui/FilePicker/FilePicker";
import useToast from "../../../../../hooks/useToast";
import { INewCollectionsNote } from "../../../types/clients.types";
import {
    useAddCollectionsNote,
    useAddCollectionsNoteDocuments,
} from "../../../hooks/clients.mutations";

interface AddCollectionsNoteProps {
    onFormSubmit(): void;
}

const acceptedFileTypes: Accept = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/heic": [".heic"],
    "image/tiff": [".tif", ".tiff"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
    ],
};

const maxFileSizeInBytes = 10 * 1024 * 1024;

const schema = yup.object({
    description: yup.string().trim().required("Description is required"),
});

const uploadConditionText =
    "Maximum file size of 10MB: .pdf, .jpg, .png, .heic, .tiff, .docx";

export default function AddCollectionsNote({
    onFormSubmit,
}: AddCollectionsNoteProps) {
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const params = useParams();
    const clientId = params.clientId as string;

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<INewCollectionsNote>({
        resolver: yupResolver(schema),
    });

    const addCollectionsNote = useAddCollectionsNote(clientId);
    const addDocuments = useAddCollectionsNoteDocuments();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const handleDrop = (acceptedFiles: File[]) => {
        setSelectedFiles((prev) =>
            prev ? [...prev, ...acceptedFiles] : [...acceptedFiles]
        );
    };

    const handleRejectedFiles = (fileRejections: FileRejection[]) => {
        const rejectedNames = fileRejections
            .map((rejection) => rejection.file.name)
            .join(", ");
        toast({
            mode: "error",
            message: `Could not attach ${rejectedNames}. ${uploadConditionText}`,
        });
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (!selectedFiles) return;
        const updated = selectedFiles.filter((file) => file.name !== filename);
        setSelectedFiles(updated.length > 0 ? updated : null);
    };

    const handleComplete = () => {
        queryClient.invalidateQueries({
            queryKey: [clientId, "collections-notes"],
        });
        onFormSubmit();
    };

    const onSubmit = (noteData: INewCollectionsNote) => {
        addCollectionsNote.mutate(noteData, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message:
                        res.message || "Collections note added successfully",
                });

                if (!selectedFiles || selectedFiles.length === 0) {
                    handleComplete();
                    return;
                }

                const formData = new FormData();
                selectedFiles.forEach((file) =>
                    formData.append("documents", file)
                );

                addDocuments.mutate(
                    {
                        collectionsNoteId: res.data.collections_note_id,
                        formData,
                    },
                    {
                        onSuccess: handleComplete,
                        onError: (err) => {
                            toast({
                                mode: "error",
                                message:
                                    err.response?.data.message ||
                                    "Could not add attachments to collections note",
                            });
                            handleComplete();
                        },
                    }
                );
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add collections note at this time",
                });
            },
        });
    };

    return (
        <form id="add-collections-note" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <TextArea
                    {...register("description")}
                    label="Description"
                    hasError={!!errors.description}
                    errorText={errors.description?.message}
                />
            </div>
            <p className="font-medium !text-caption text-textSec flex items-center gap-x-1 mb-1">
                Attachments
                <span className="text-textSec text-small" aria-label="optional">
                    (optional)
                </span>
            </p>
            <FilePicker.FilePicker
                onDrop={handleDrop}
                accept={acceptedFileTypes}
                maxSize={maxFileSizeInBytes}
                onDropRejected={handleRejectedFiles}
                disabled={
                    addCollectionsNote.isLoading || addDocuments.isLoading
                }
            >
                <FilePicker.FilePickerIcon />
                <FilePicker.FilePickerMeta
                    description="Click to upload"
                    uploadConditionText={uploadConditionText}
                />
            </FilePicker.FilePicker>
            <FilePicker.FilePickerSelectedFiles
                selectedFiles={selectedFiles}
                handleRemoveSelectedFile={handleRemoveSelectedFile}
                wrapperClassName="mt-[--spacing-form-items]"
            />
        </form>
    );
}
